import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Group, Stack, Text, Badge } from '@mantine/core';
import { STAGE_CONFIG } from '../constants/pipeline';
import { LeadCard } from './LeadCard';

function SortableCard({ lead, campaigns }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(lead.id) });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
      {...attributes}
      {...listeners}
    >
      <LeadCard lead={lead} campaigns={campaigns} />
    </div>
  );
}

function StageColumn({ stage, leads, campaigns, stageCounts }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const badgeValue = stageCounts
    ? (stageCounts[stage.id] ?? 0)
    : leads.length;

  return (
    <Box mb="sm">
      <Group
        px="sm"
        py={6}
        style={{
          borderRadius: '8px 8px 0 0',
          background: `var(--mantine-color-${stage.color}-1)`,
        }}
      >
        <Text size="sm" fw={600} c={`${stage.color}.8`}>{stage.label}</Text>
        <Badge size="xs" color={stage.color} variant="filled" circle>
          {badgeValue}
        </Badge>
      </Group>

      <Box
        ref={setNodeRef}
        style={{
          minHeight: 80,
          borderRadius: '0 0 8px 8px',
          background: isOver
            ? 'var(--mantine-color-aviary-0)'
            : 'var(--mantine-color-gray-0)',
          border: isOver
            ? '2px dashed var(--mantine-color-aviary-4)'
            : '2px dashed transparent',
          transition: 'background 120ms, border 120ms',
        }}
      >
        <SortableContext
          items={leads.map((l) => String(l.id))}
          strategy={verticalListSortingStrategy}
        >
          <Group gap="xs" wrap="wrap" align="flex-start">
            {leads.map((lead) => (
              <SortableCard key={lead.id} lead={lead} campaigns={campaigns} />
            ))}
            {leads.length === 0 && !isOver && (
              <Text size="xs" c="dimmed" py={6} px={4}>No leads</Text>
            )}
          </Group>
        </SortableContext>
      </Box>
    </Box>
  );
}

export default function PipelineBoard({ leads, onStageDrop, campaigns, stageCounts }) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const byStage = STAGE_CONFIG.reduce((acc, s) => {
    acc[s.id] = leads
      .filter((l) => l.stage === s.id)
      .sort((a, b) => {
        if (!a.nextActionAt && !b.nextActionAt) return 0;
        if (!a.nextActionAt) return 1;
        if (!b.nextActionAt) return -1;
        return new Date(a.nextActionAt) - new Date(b.nextActionAt);
      });
    return acc;
  }, {});

  function findStage(id) {
    const normalizedId = String(id);
    if (STAGE_CONFIG.find((s) => s.id === id)) return id;
    for (const [stageId, stageLeads] of Object.entries(byStage)) {
      if (stageLeads.find((l) => String(l.id) === normalizedId)) return stageId;
    }
    return null;
  }

  function handleDragStart({ active }) {
    setActiveId(active.id);
  }

  function handleDragEnd({ active, over }) {
    setActiveId(null);
    if (!over) return;
    const fromStage = findStage(active.id);
    const toStage = findStage(over.id);
    if (toStage && toStage !== fromStage) {
      const sourceLead = leads.find((l) => String(l.id) === String(active.id));
      onStageDrop(sourceLead ? sourceLead.id : active.id, toStage);
    }
  }

  const activeLead = leads.find((l) => String(l.id) === String(activeId));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Stack gap={0} p="md">
        {STAGE_CONFIG.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            leads={byStage[stage.id]}
            campaigns={campaigns}
            stageCounts={stageCounts}
          />
        ))}
      </Stack>

      <DragOverlay>
        {activeLead ? <LeadCard lead={activeLead} isDragging campaigns={campaigns} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
